import React from "react"
import PropTypes from "prop-types"
import { Flex, Timeline, Typography, Empty } from "antd"
import { spacings } from "themes/variables"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import styles from "./VersionHistory.module.css"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import CustomPagination from "components/common/CustomPagination"
import { useTranslation } from "react-i18next"

dayjs.extend(relativeTime)

const { Text } = Typography

const VersionHistory = ({ versions, loading, pagination, onPageChange, onPageSizeChange, pageSize }) => {
  const { t } = useTranslation()

  if (loading) {
    return <FirstLoading loading={loading} />
  }

  if (!versions || versions.length === 0) {
    return <Empty description={t("calendar.journal.no_version_history")} />
  }

  const formatFieldName = (field) => {
    return field.charAt(0).toUpperCase() + field.slice(1)
  }

  const formatValue = (value) => {
    if (value === null || value === undefined) {
      return (
        <Text type="secondary" italic>
          {t("calendar.journal.empty_value")}
        </Text>
      )
    }
    return <Text strong>{value}</Text>
  }

  const renderChangeItem = (field, change) => {
    return (
      <div key={field} className={styles.changeItem}>
        <Text className={styles.fieldName}>{formatFieldName(field)}:</Text>
        <Flex gap={spacings.XS} align="center" className={styles.changeValues}>
          {formatValue(change.from)}
          <Text type="secondary">→</Text>
          {formatValue(change.to)}
        </Flex>
      </div>
    )
  }

  const timelineItems = versions.map((version) => {
    const changeCount = Object.keys(version.changes || {})?.length

    return {
      key: version.id,
      color: "blue",
      children: (
        <Flex vertical gap={spacings.S}>
          <Flex justify="space-between" align="center">
            <Text type="secondary">{dayjs(version.created_at).format("MMM DD, YYYY [at] h:mm A")}</Text>
            <Text type="secondary" className={styles.timeAgo}>
              {dayjs(version.created_at).fromNow()}
            </Text>
          </Flex>
          {version?.changed_by && (
            <Text type="secondary">
              {t("calendar.journal.changed_by")} <Text strong>{version.changed_by?.email}</Text>
            </Text>
          )}
          {changeCount > 0 ? (
            <Flex vertical gap={spacings.XS} className={styles.changesContainer}>
              <Text type="secondary" className={styles.changesLabel}>
                {changeCount} {changeCount === 1 ? t("calendar.journal.field_changed") : t("calendar.journal.fields_changed")}:
              </Text>
              {Object.entries(version.changes).map(([field, change]) => renderChangeItem(field, change))}
            </Flex>
          ) : (
            <Text type="secondary" italic>
              {t("calendar.journal.no_changes_recorded")}
            </Text>
          )}
        </Flex>
      )
    }
  })

  return (
    <Flex vertical gap={spacings.M}>
      <div className={styles.versionHistoryContainer}>
        <Timeline items={timelineItems} className={styles.timeline} />
      </div>
      {pagination && pagination.total_count > 0 && (
        <CustomPagination
          metadata={{
            page: pagination.current_page,
            total_pages: pagination.total_pages,
            next_page: pagination.current_page < pagination.total_pages ? pagination.current_page + 1 : null,
            prev_page: pagination.current_page > 1 ? pagination.current_page - 1 : null
          }}
          pageSize={pageSize}
          total={pagination.total_count}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          pageSizeOptions={[10, 25, 50, 100]}
          showPageSizeSelector={true}
        />
      )}
    </Flex>
  )
}

VersionHistory.propTypes = {
  versions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      created_at: PropTypes.string.isRequired,
      changed_by: PropTypes.string,
      changes: PropTypes.object.isRequired
    })
  ),
  loading: PropTypes.bool,
  pagination: PropTypes.shape({
    current_page: PropTypes.number,
    total_pages: PropTypes.number,
    total_count: PropTypes.number,
    per_page: PropTypes.number
  }),
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  pageSize: PropTypes.number
}

VersionHistory.defaultProps = {
  versions: [],
  loading: false,
  pagination: null,
  onPageChange: () => {},
  onPageSizeChange: () => {},
  pageSize: 25
}

export default VersionHistory
